import React, { useState, useEffect } from 'react';
import { 
  Key, 
  Copy, 
  Eye, 
  EyeOff, 
  Plus, 
  MoreVertical,
  Trash2,
  RotateCcw,
  Shield,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { CreateApiKeyModal } from './CreateApiKeyModal';
import platformApi, { type ApiKey, PlatformApiService } from '../services/platformApi';

// Remove the local interface since we're importing it from the service

export const ApiKeysPage: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load API keys on component mount
  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await platformApi.getApiKeys();
      // Filter out revoked keys to only show active keys
      setApiKeys(response.apiKeys.filter(key => key.status !== 'revoked'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
      console.error('Failed to load API keys:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleKeyVisibility = (keyId: string) => {
    const newVisible = new Set(visibleKeys);
    if (newVisible.has(keyId)) {
      newVisible.delete(keyId);
    } else {
      newVisible.add(keyId);
    }
    setVisibleKeys(newVisible);
  };

  const copyToClipboard = async (key: string, keyId: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(keyId);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const maskKey = (keyPrefix: string) => {
    // API response already provides a masked key_prefix
    return keyPrefix;
  };

  const getUsagePercentage = (current: number, limit?: number) => {
    if (!limit) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const getStatusColor = (status: string) => {
    return status === 'active' 
      ? 'text-success bg-green-50 dark:bg-green-900/20' 
      : 'text-warning bg-yellow-50 dark:bg-yellow-900/20';
  };

  const handleCreateKey = async (keyData: any) => {
    try {
      setError(null);
      const newKey = await platformApi.createApiKey({
        name: keyData.name,
        permissions: keyData.permissions,
        usageLimit: keyData.usageLimit,
        expiresIn: keyData.expiresIn
      });
      
      // Don't reload API keys here - wait until modal closes
      // This prevents parent re-render that could interfere with modal state
      
      // Return the new key so the modal can display it
      // The API returns the full key in the 'key' field during creation
      return { key: newKey.key || '' };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
      console.error('Failed to create API key:', err);
      throw err; // Re-throw so the modal can handle the error
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      await platformApi.deleteApiKey(keyId);
      
      // Reload API keys to get updated list (now filtered to exclude revoked keys)
      await loadApiKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete API key');
      console.error('Failed to delete API key:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        <span className="ml-2 text-light-text-secondary dark:text-dark-text-secondary">
          Loading API keys...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <div>
              <h3 className="font-medium text-red-900 dark:text-red-100">
                Error loading API keys
              </h3>
              <p className="text-sm text-red-700 dark:text-red-200 mt-1">
                {error}
              </p>
              <button 
                onClick={loadApiKeys}
                className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 mt-2"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-light-text-primary dark:text-dark-text-primary">
            API Keys
          </h1>
          <p className="text-light-text-secondary dark:text-dark-text-secondary mt-2">
            Manage your API keys to authenticate requests to Customer Connects
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          <Plus size={20} className="mr-2" />
          Create API Key
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Total API Keys
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {apiKeys.length}
              </p>
            </div>
            <Key className="w-8 h-8 text-primary-500" />
          </div>
        </div>
        
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Active Keys
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {apiKeys.filter(k => k.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-success" />
          </div>
        </div>
        
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Total Usage
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {apiKeys.reduce((sum, key) => sum + key.currentUsage, 0).toLocaleString()}
              </p>
            </div>
            <Shield className="w-8 h-8 text-info" />
          </div>
        </div>
      </div>

      {/* API Keys List */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
        <div className="px-6 py-4 border-b border-light-border dark:border-dark-border">
          <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
            Your API Keys
          </h2>
        </div>
        
        <div className="divide-y divide-light-border dark:divide-dark-border">
          {apiKeys.map((key) => (
            <div key={key.id} className="p-6 hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">
                      {key.name}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(key.status)}`}>
                      {key.status}
                    </span>
                    {key.status === 'active' && key.usageLimit && getUsagePercentage(key.currentUsage, key.usageLimit) > 80 && (
                      <AlertTriangle className="w-4 h-4 text-warning" />
                    )}
                  </div>
                  
                  {/* API Key Display */}
                  <div className="flex items-center space-x-2 mb-3">
                    <code className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-3 py-2 rounded font-mono text-sm text-light-text-primary dark:text-dark-text-primary border border-light-border dark:border-dark-border">
                      {visibleKeys.has(key.id) ? key.keyPrefix : maskKey(key.keyPrefix)}
                    </code>
                    <button
                      onClick={() => toggleKeyVisibility(key.id)}
                      className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
                      title={visibleKeys.has(key.id) ? 'Hide key prefix' : 'Show key prefix'}
                    >
                      {visibleKeys.has(key.id) ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button
                      onClick={() => copyToClipboard(key.keyPrefix, key.id)}
                      className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
                      title="Copy key prefix to clipboard"
                    >
                      {copiedKey === key.id ? (
                        <CheckCircle size={16} className="text-success" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>
                  
                  {/* Security Notice */}
                  <div className="mb-3">
                    <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                      <Shield className="w-3 h-3 inline mr-1" />
                      Full API key was only shown once during creation for security
                    </p>
                  </div>
                  
                  {/* Usage Bar */}
                  {key.usageLimit && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-light-text-secondary dark:text-dark-text-secondary">
                          Usage: {key.currentUsage.toLocaleString()} / {key.usageLimit.toLocaleString()}
                        </span>
                        <span className="text-light-text-secondary dark:text-dark-text-secondary">
                          {Math.round(getUsagePercentage(key.currentUsage, key.usageLimit))}%
                        </span>
                      </div>
                      <div className="w-full bg-light-bg-quaternary dark:bg-dark-bg-quaternary rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            getUsagePercentage(key.currentUsage, key.usageLimit) > 80 
                              ? 'bg-warning' 
                              : 'bg-primary-500'
                          }`}
                          style={{ width: `${getUsagePercentage(key.currentUsage, key.usageLimit)}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Key Info */}
                  <div className="flex items-center space-x-6 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                    <div className="flex items-center space-x-1">
                      <Calendar size={14} />
                      <span>Created {new Date(key.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div>Last used {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : 'Never'}</div>
                    <div>{PlatformApiService.parsePermissions(key.permissions).length} permissions</div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:bg-light-bg-quaternary dark:hover:bg-dark-bg-quaternary rounded">
                    <RotateCcw size={16} />
                  </button>
                  <button 
                    onClick={() => handleDeleteKey(key.id)}
                    className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-error hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    title="Delete API Key"
                  >
                    <Trash2 size={16} />
                  </button>
                  <button className="p-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:bg-light-bg-quaternary dark:hover:bg-dark-bg-quaternary rounded">
                    <MoreVertical size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
              Keep your API keys secure
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-200">
              Never share your API keys in public repositories, client-side code, or unsecured locations. 
              Treat them like passwords and store them securely in environment variables.
            </p>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      <CreateApiKeyModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          // Reload API keys when modal closes to refresh the list
          loadApiKeys();
        }}
        onCreateKey={handleCreateKey}
      />
    </div>
  );
};